import type { Channel } from '../../../redux/customers/customerTypes';

/**
 * Targeted Campaigns — entity types.
 * Field names follow the requirements contract in camelCase; enum values are verbatim.
 * Money is integer cents. Timestamps are UTC ISO 8601 strings.
 */

// Customers, their consent and segments belong to the Customers module; Campaigns reads them through Redux.
export type {
  AudienceCounts, Channel, ConsentRecord, ConsentSource, ConsentStatus, Customer, CustomerOrder, Daypart, OrderChannel,
  RuleCondition, RuleField, RuleGroup, RuleNode, RuleOperator, RuleValue, Segment, SegmentType,
} from '../../../redux/customers/customerTypes';
export { CHANNELS } from '../../../redux/customers/customerTypes';

interface SystemFields {
  id: string;
  brandId: string;
  createdAt: string;
  updatedAt: string;
}

// ── Campaigns ──

export type CampaignKind = 'one_time' | 'automation';

export type CampaignStatus =
  | 'draft' | 'scheduled' | 'sending' | 'active' | 'paused' | 'completed' | 'cancelled' | 'archived';

export type CampaignAction =
  | 'schedule' | 'send_now' | 'enable' | 'unschedule' | 'cancel' | 'pause' | 'resume' | 'archive';

export type TriggerType =
  | 'welcome' | 'post_visit' | 'win_back' | 'birthday' | 'loyalty_welcome'
  | 'near_reward' | 'reward_unlocked' | 'points_expiring' | 'high_spender_thanks';

export type ChannelMode = 'first_available' | 'all_selected';

export type EmailTemplateId = 'promo_basic' | 'announcement' | 'reward';
export type LinkTarget = 'online_ordering' | 'menu' | 'loyalty' | 'offers' | 'reservation' | 'get_app';

export interface SmsContent { body: string }
export interface EmailContent {
  subject: string;
  preheader: string;
  templateId: EmailTemplateId;
  headline: string;
  body: string;
  imageUrl: string;
  ctaLabel: string;
  ctaTarget: LinkTarget;
}
export interface PushContent { title: string; body: string; deepLink: LinkTarget }

export interface CampaignContent {
  sms?: SmsContent;
  email?: EmailContent;
  push?: PushContent;
}

export type RewardType = 'none' | 'offer' | 'bonus_points' | 'points_multiplier' | 'free_reward';
export type OfferDelivery = 'auto_apply' | 'unique_code';
/** Where a campaign code can be redeemed. */
export type RedeemIn = 'anywhere' | 'app_only';

export interface RewardConfig {
  offerId?: string;
  /** Snapshot of the offer name so lists and reports can show it without the Offers slice. */
  offerName?: string;
  delivery?: OfferDelivery;
  redeemIn?: RedeemIn;
  validDays?: number;
  maxRedemptionsPerCustomer?: number;
  points?: number;
  multiplier?: number;
  loyaltyRewardId?: string;
  loyaltyRewardName?: string;
}

export interface TriggerConfig {
  /** Days — delay after the event, or the N threshold, depending on the trigger. */
  days?: number;
  /** AUT-06 — percent of the reward cost. */
  percent?: number;
}

export interface Campaign extends SystemFields {
  name: string;
  kind: CampaignKind;
  status: CampaignStatus;
  segmentId: string | null;
  triggerType: TriggerType | null;
  triggerConfig: TriggerConfig;
  /** Empty means all outlets. */
  outletIds: string[];
  /** Order is the fallback priority. */
  channels: Channel[];
  channelMode: ChannelMode;
  content: CampaignContent;
  rewardType: RewardType;
  rewardConfig: RewardConfig;
  scheduledAt: string | null;
  attributionWindowDays: number;
  holdoutPercent: number;
  createdBy: string;
  approvedBy: string | null;
  // List/read-model fields (RPT-14, UI-08)
  lastSentAt: string | null;
  audienceCount: number;
  attributedRevenueCents: number;
  sentLast30Days: number;
  revenueLast30DaysCents: number;
  /** SMS cost plus discounts and loyalty rewards given. */
  spendCents: number;
  /** The status a paused campaign returns to on resume. */
  pausedFrom: 'sending' | 'active' | null;
}

export type CampaignDraft = Omit<Campaign, keyof SystemFields | 'lastSentAt' | 'audienceCount' | 'attributedRevenueCents' | 'sentLast30Days' | 'revenueLast30DaysCents' | 'spendCents' | 'pausedFrom' | 'approvedBy' | 'createdBy' | 'status'>;

// ── Recipients, messages, grants, attribution ──

export type SkipReason =
  | 'blocked' | 'no_consent' | 'no_address' | 'bad_address' | 'frequency_cap'
  | 'holdout' | 'duplicate' | 'no_credits' | 'quiet_hours_expired';

export const SKIP_REASONS: SkipReason[] = [
  'blocked', 'no_consent', 'no_address', 'bad_address', 'frequency_cap',
  'holdout', 'duplicate', 'no_credits', 'quiet_hours_expired',
];

export type RecipientStatus = 'selected' | 'sent' | 'skipped';
export type MessageStatus = 'queued' | 'sent' | 'delivered' | 'failed' | 'bounced' | 'opened' | 'clicked' | 'opted_out';

/** CampaignRecipient joined with its message, as the recipient table shows it (RPT-16). */
export interface RecipientRow extends SystemFields {
  campaignId: string;
  customerId: string;
  customerName: string;
  runId: string;
  isHoldout: boolean;
  status: RecipientStatus;
  skipReason: SkipReason | null;
  channel: Channel | null;
  messageStatus: MessageStatus | null;
  smsSegments: number | null;
  rewardGrantId: string | null;
  /** The recipient's unique code and what became of it, when the reward is a code. */
  rewardCode: string | null;
  rewardStatus: GrantStatus | null;
}

export type GrantStatus = 'active' | 'redeemed' | 'expired' | 'revoked';

export interface RewardGrant extends SystemFields {
  campaignId: string;
  campaignName: string;
  customerId: string;
  type: RewardType;
  offerId: string | null;
  code: string | null;
  label: string;
  expiresAt: string;
  status: GrantStatus;
  redeemedOrderId: string | null;
}

export type AttributionMethod = 'redemption' | 'click' | 'window';

export interface ChannelFunnel {
  sent: number;
  delivered: number;
  opened: number;
  clicked: number;
}

/** RPT-01 … RPT-13 */
export interface CampaignReport {
  id: string;
  campaignId: string;
  createdAt: string;
  updatedAt: string;
  audience: number;
  perChannel: Record<Channel, ChannelFunnel>;
  skipped: Record<SkipReason, number>;
  uniqueClicks: number;
  optedOut: number;
  redemptions: number;
  unredeemedGrants: number;
  attributedOrders: Record<AttributionMethod, number>;
  revenueCents: number;
  discountCents: number;
  smsCredits: number;
  /** What the campaign cost: SMS, discounts given and the value of loyalty rewards. */
  smsCostCents: number;
  rewardCostCents: number;
  spendCents: number;
  holdout: { size: number; holdoutOrderRate: number; recipientOrderRate: number } | null;
}

// ── Settings ──

export interface BrandCampaignSettings extends SystemFields {
  frequencyCapCount: number;
  frequencyCapDays: number;
  /** HH:mm, customer local time */
  quietStartLocal: string;
  quietEndLocal: string;
  smsCreditsBalance: number;
  /** Price of one SMS credit, used to show SMS cost in money. May be a fraction of a cent. */
  smsCreditPriceCents: number;
  emailFromName: string;
  emailReplyTo: string;
  postalAddress: string;
  featureEnabled: boolean;
  defaultAttributionWindowDays: number;
  /** AUT-10 — HH:mm, brand timezone */
  automationCheckTime: string;
  brandName: string;
  brandTimezone: string;
}

export interface CreditHistoryEntry extends SystemFields {
  at: string;
  type: 'purchase' | 'usage' | 'refund';
  credits: number;
  balanceAfter: number;
  note: string;
}

/** A block of SMS credits that can be bought (decision D1: prepaid credits). */
export interface CreditPack {
  id: string;
  credits: number;
  priceCents: number;
}

// ── Opt-out review queue (CON-09) ──

export type InboundStatus = 'pending' | 'opted_out' | 'dismissed';

export interface InboundSms extends SystemFields {
  customerId: string;
  customerName: string;
  phoneE164: string;
  body: string;
  receivedAt: string;
  status: InboundStatus;
}

// ── Overview (RPT-17) ──

/** One campaign's results inside the report's date range. */
export interface CampaignPerformanceRow {
  id: string;
  name: string;
  kind: CampaignKind;
  sentAt: string | null;
  sent: number;
  delivered: number;
  clicked: number;
  redemptions: number;
  optedOut: number;
  orders: number;
  spendCents: number;
  revenueCents: number;
}

export interface OverviewStats {
  id: string;
  createdAt: string;
  updatedAt: string;
  rangeDays: number;
  sent: Record<Channel, number>;
  revenueCents: number;
  spendCents: number;
  orders: number;
  optOutRate: number;
  linkedOrderRate: number;
  reachable: Record<Channel, number>;
  totalCustomers: number;
  series: { date: string; sms: number; email: number; push: number }[];
  campaigns: CampaignPerformanceRow[];
}

// ── Tracking sources ──

export type SourcePlatform = 'instagram' | 'facebook' | 'google' | 'website' | 'print' | 'other';
export type SourceStatus = 'active' | 'paused';

/**
 * A place outside our own messages where the brand shares a link or code, e.g. an Instagram bio.
 * One link and, optionally, one promo code per source; many people use the same ones.
 */
export interface TrackingSource extends SystemFields {
  name: string;
  platform: SourcePlatform;
  note: string;
  linkTarget: LinkTarget;
  /** Random token of the short link; carries no customer or source details. */
  linkToken: string;
  promoCode: string | null;
  offerId: string | null;
  offerName: string | null;
  status: SourceStatus;
  clicks: number;
  /** Orders placed in the same visit as a click, or with the source's code. */
  orders: number;
  codeRedemptions: number;
  /** Customers whose first order came through this source. */
  newCustomers: number;
  revenueCents: number;
  discountCents: number;
  lastClickAt: string | null;
}

// ── Reference data shown in pickers ──


export interface CampaignToast {
  id: string;
  message: string;
  variant: 'success' | 'error' | 'warning' | 'info';
}
